import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type OutletResource,
  type TerminalResource,
  TerminalsController_create_v1,
  type TerminalsController_create_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';

/**
 * Creating a terminal: `POST /v1/terminals`, three fields.
 *
 * **The 409 belongs to the prefix field.** `IDENTITY_TERMINAL_PREFIX_TAKEN` is a
 * statement about one input, and the difference between rendering it next to
 * that input and rendering it above the form is the difference between a person
 * correcting five characters and a person re-reading three fields to work out
 * which one the server meant.
 *
 * **The permanence is said before the refusal is.** A prefix is taken for all
 * time — it is printed on tickets and receipts that outlive the device, so the
 * API never releases one, not even after revocation. Somebody who learns that
 * only from a 409 tries the same handful of near-misses; somebody who reads it
 * under the field picks a fresh one first time. Note that this is the **opposite**
 * of the outlet-code rule next door, where retiring an outlet does release its
 * code, which is exactly why neither hint leans on the other.
 *
 * **A retired outlet is refused in the picker, not in a response.** The API
 * answers `RESOURCE_NOT_FOUND` for one, indistinguishable from an outlet that
 * never existed, so a form that offered them would ask somebody to fill in three
 * fields to be told the place they chose is not a place. The screen passes only
 * the outlets still trading; the 404 arm below is the backstop for an outlet
 * retired while this form was open, and it lands on the picker for the same
 * reason the 409 lands on the prefix.
 *
 * **The copy is never written here.** `messageForApiError` turns the code into a
 * sentence and it is the only thing that does; the code is what is branched on,
 * never the message and never the status. A 409 from a proxy is not this refusal.
 */

const PREFIX_REQUIRED = 'Enter a prefix for this terminal.';
const NAME_REQUIRED = 'Enter a name for this terminal.';
const OUTLET_REQUIRED = 'Choose the outlet this terminal stands in.';

/**
 * What this dialog will send, and what it refuses before sending.
 *
 * **No shape rule for the prefix**, and it is not an oversight: the published
 * contract carries no pattern for it, and the one thing this app knows about a
 * prefix — that it is taken for ever — is a fact about the whole set rather than
 * about the characters. It is not in `field-schemas.ts` for the same reason;
 * nothing else in the app sets one, and a fragment shared by one caller is a
 * fragment invented for tidiness.
 */
const newTerminalSchema = z.object({
  outletId: z.string().min(1, OUTLET_REQUIRED),
  prefix: z.string().trim().min(1, PREFIX_REQUIRED),
  name: z.string().trim().min(1, NAME_REQUIRED),
});

type NewTerminalValues = z.infer<typeof newTerminalSchema>;

export function NewTerminalDialog({
  open,
  onOpenChange,
  outlets,
  defaultOutletId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** The outlets that may be created against — the ones still trading. */
  outlets: readonly OutletResource[];

  /** Prefilled from the list's filter when one is set. */
  defaultOutletId: string | null;

  onCreated: (terminal: TerminalResource) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>New terminal</DialogTitle>
          <DialogDescription>
            One till or gate device, standing in one outlet. This creates the record; the machine is
            registered on the device itself afterwards, and the terminal cannot take money until it
            is.
          </DialogDescription>
        </DialogHeader>

        {/* The form is a child so that closing the dialog unmounts it and the
            next open starts empty. Holding the fields in this component would
            leave a half-typed terminal — and a refusal about it — waiting the
            next time somebody opened the form. */}
        <NewTerminalForm
          outlets={outlets}
          defaultOutletId={defaultOutletId}
          onCreated={onCreated}
        />
      </FormDialogContent>
    </Dialog>
  );
}

function NewTerminalForm({
  outlets,
  defaultOutletId,
  onCreated,
}: {
  outlets: readonly OutletResource[];
  defaultOutletId: string | null;
  onCreated: (terminal: TerminalResource) => void;
}) {
  const client = useApiClient();

  // The filter's outlet if it is one that may be created against, otherwise the
  // only one there is, otherwise nothing chosen. `outlets[0]?.id` rather than an
  // index read, because `noUncheckedIndexedAccess` is on and an empty park is a
  // real state — see the panel below.
  const form = useForm({
    resolver: zodResolver(newTerminalSchema),
    mode: 'onTouched',
    defaultValues: {
      outletId:
        defaultOutletId !== null && outlets.some((outlet) => outlet.id === defaultOutletId)
          ? defaultOutletId
          : outlets.length === 1
            ? (outlets[0]?.id ?? '')
            : '',
      prefix: '',
      name: '',
    },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // One key for as long as this form is open. The dialog unmounts on success, so
  // the next attempt gets a fresh one; a refusal rolls the claim back
  // server-side, so a corrected resubmit re-claims cleanly. Where this component
  // makes more than one kind of write, the same key is safe across them: the
  // API's record is unique on `(scope, key)`, and each of those writes is its own
  // scope.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const pending = form.formState.isSubmitting;

  // A terminal stands in an outlet, so with nothing trading there is nowhere to
  // put one. Said here with the submit disabled, rather than as a 404 after
  // three fields — and it names the fix, because the person reading it is the
  // one who can do it.
  const nowhereToPutIt = outlets.length === 0;

  // The picker's value is an outlet id, and Base UI resolves the trigger's label
  // from this prop rather than from the `<SelectItem>`s below it. Without it the
  // trigger shows the id — a bare UUID sitting where an outlet name belongs.
  const outletItems = useMemo(
    () => outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [outlets],
  );

  const handleSubmit = async ({ outletId, prefix, name }: NewTerminalValues) => {
    // Belt and braces with the disabled button below. A second submit of the
    // same prefix is refused by a rule that never releases it, so the retry
    // that would fix an ordinary double-submit cannot fix this one.
    if (pending || nowhereToPutIt) return;

    setFormError(null);

    try {
      // The body's shape comes from the generated client. Nothing about
      // `POST /v1/terminals` is written down in this file. Both strings arrive
      // trimmed, because the schema trims them rather than this function.
      const body: TerminalsController_create_v1Body = { outletId, prefix, name };

      onCreated(
        await client.request(TerminalsController_create_v1, {
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'IDENTITY_TERMINAL_PREFIX_TAKEN') {
        // Beside the field, and the focus goes with it. Somebody who submitted
        // with the keyboard is otherwise left with the caret on the button and
        // a message they have to go and find.
        form.setError('prefix', { message: messageForFailure(error) });
        form.setFocus('prefix');
      } else if (error instanceof ApiError && error.code === 'RESOURCE_NOT_FOUND') {
        // The outlet was retired, or removed, while this form was open. The
        // only field it can be about is the picker.
        form.setError('outletId', { message: messageForFailure(error) });
      } else {
        setFormError(messageForFailure(error));
      }

      // Nothing is cleared. The retry is one corrected field, and re-typing the
      // other two to fix it is a cruelty this form is in no position to inflict.
    }
  };

  return (
    // One column, per docs/ui-conventions.md. `noValidate` because the browser's
    // own bubbles are not this app's error language and cannot be worded.
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="outletId"
          render={({ field }) => (
            <FormItem>
              <FormLabel required id="terminal-outlet-label" className={fieldLabel}>
                Outlet
              </FormLabel>
              {/* Not wrapped in `FormControl`: the trigger is a button rather
                  than the input the generated id belongs on. It is described by
                  the message by hand for that reason, and only when there is
                  one. */}
              <Select
                value={field.value}
                items={outletItems}
                onValueChange={(next) => {
                  // Base UI types the value as nullable because a select may be
                  // cleared. `''` is what this form calls "nothing chosen", and
                  // the schema refuses it — so a clear lands on the same path as
                  // never having chosen, rather than on a `null` in the body.
                  field.onChange(next ?? '');
                }}
                disabled={nowhereToPutIt}
              >
                <SelectTrigger
                  id="terminal-outlet"
                  aria-label="Outlet"
                  aria-invalid={form.formState.errors.outletId !== undefined}
                  aria-describedby={
                    form.formState.errors.outletId === undefined
                      ? undefined
                      : 'terminal-outlet-error'
                  }
                  className={`${fieldSurface} h-11 w-full`}
                >
                  <SelectValue placeholder="Choose an outlet" />
                </SelectTrigger>
                <SelectContent>
                  {/* Only the outlets still trading. A retired one is refused by
                      the API and is not offered here, so the refusal never has
                      to happen. */}
                  <SelectGroup>
                    {outlets.map((outlet) => (
                      <SelectItem key={outlet.id} value={outlet.id}>
                        {outlet.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormMessage id="terminal-outlet-error" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="prefix"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Terminal prefix
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // A prefix is read off a receipt and typed back in: mono
                    // face, tabular figures.
                    className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                    required
                    placeholder="GATE1"
                    autoComplete="off"
                    autoCapitalize="characters"
                    {...field}
                    onChange={(event) => {
                      // Uppercased as it is typed, because the API stores it
                      // uppercase. Left alone, somebody types `doll1`, the list
                      // comes back saying `DOLL1`, and the screen looks like it
                      // changed their answer.
                      field.onChange(event.target.value.toUpperCase());
                    }}
                  />
                }
              />
              <FormDescription>
                Printed on every ticket and receipt this terminal issues, and never reused — not
                even after the device is revoked. Choose one you will not want back.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Name
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11`}
                    required
                    placeholder="Lane 1"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormDescription>
                How staff refer to the machine — the lane, the counter, the desk it sits on.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {nowhereToPutIt && (
          // Not an `Alert`: nothing has been refused and nobody has done
          // anything wrong. It is the state of the park, stated where the choice
          // would have been. `text-foreground` on `bg-muted` — the muted-on-muted
          // pairing misses AA and is the one this panel invites.
          <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
            A terminal stands in an outlet, and no outlet is trading. Create an outlet first, then
            come back and give it a terminal.
          </p>
        )}

        {/* Above the submit and it stays there — a toast vanishes, and somebody
            who looked away has no way to get it back. */}
        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending || nowhereToPutIt}>
            {pending ? 'Creating…' : 'Create terminal'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
